import { Injectable } from '@angular/core';
import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { SSEClientTransport } from "@modelcontextprotocol/sdk/client/sse.js";
import { QueueService } from './queue.service';
import { AiService } from './ai/ai.service';
import { Mutex } from './mutex.service';
import { RepoService } from './repo.service';

type CallToolResponse = {
  content: {
    text: string;
  }[];
  isError: boolean;
}

type McpToolSchema = {
  name: string;
  inputSchema?: {
    properties?: Record<string, unknown>;
  };
};

@Injectable({
  providedIn: 'root'
})
export class McpService {
  client: Client = new Client({
    name: 'clinical-studio-client',
    version: '0.0.1',
    description: 'A Clinical AI Studio client',
  });
  transport: SSEClientTransport | null = null;
  isConnected: boolean = false;
  private toolSchemas = new Map<string, McpToolSchema>();

  constructor(
    private queue: QueueService,
    private aiService: AiService,
    private mutex: Mutex,
    private repoService: RepoService
  ) {

    this.aiService.aiWritingChunk$.subscribe(async (chunk) => {
      if (chunk.section === 'mcp' && chunk.fullContent) {
        const release = await this.mutex.acquire();
        await this.callMcpTool(chunk.uri!, chunk.fullContent);
        release();
      }
    });
  }

  async connect() {
    if (!this.repoService.repo()?.url) {
      throw new Error('No repository URL configured', { cause: { code: 'MCP_NO_REPO_URL' } });
    }
    const url = this.repoService.repo()?.url! + '/mcp/sse';
    const token = sessionStorage.getItem('userAccessToken') || '';
    this.transport = new SSEClientTransport(new URL(url), {
      requestInit: { headers: { "Authorization": `Bearer ${token}` } },
      eventSourceInit: {
        fetch: (url, init) => {
          const headers = new Headers(init?.headers || {});
          headers.set("Authorization", `Bearer ${token}`);
          return fetch(url, {
            ...init,
            headers
          });
        }
      }
    });
    // this.transport2 = new StreamableHTTPClientTransport(new URL(url),
    //   );
    try {
      await this.client.connect(this.transport)
      // console.log('Connected to Model Context Protocol server at', url);
      this.isConnected = true;
      const tools = await this.client.listTools();
      this.toolSchemas = new Map(
        tools.tools.map(tool => [tool.name, tool as McpToolSchema]),
      );
      localStorage.setItem('mcpTools', JSON.stringify(tools));
    } catch (error) {
      if (error instanceof Error && error.message.includes('Failed to connect')) {
        throw error;
      }
      console.error('Error listing tools:', error);
      throw new Error('Failed to load MCP tools', {
        cause: { code: 'MCP_TOOL_LIST_ERROR', error },
      });
    }

  }

  disconnect() {
    if (!this.transport) {
      console.log('No transport to disconnect from');
      return;
    }
    this.transport.close().then(() => {
      console.log('Disconnected from Model Context Protocol server');
    }
    ).catch((error) => {
      console.error('Error disconnecting from Model Context Protocol server:', error);
    });
  }

  async callMcpTool(tool: string, args: string): Promise<string> {
    if (!this.transport) {
      throw new Error('Not connected to Model Context Protocol server');
    }
    // @ts-expect-error: callTool response type is not compatible with CallToolResponse
    // eslint-disable-next-line
    const response: CallToolResponse = await this.client.callTool({
      name: tool,
      arguments: this.filterToolArguments(tool, JSON.parse(args)),
    });

    if (response.isError) {
      this.queue.add({
        type: 'error',
        content: response.content[0].text,
      });
    } else {
      this.queue.add({
        type: 'mcpToolResponse',
        tool: tool,
        output: response.content[0].text,
      });
    }

    return response.content[0].text;
  }

  private filterToolArguments(
    tool: string,
    args: Record<string, unknown>,
  ): Record<string, unknown> {
    const properties = this.toolSchemas.get(tool)?.inputSchema?.properties;
    if (!properties) {
      return args;
    }
    return Object.fromEntries(
      Object.entries(args).filter(([name]) => name in properties),
    );
  }

  async callMcpToolRaw(tool: string, args: { [x: string]: unknown }): Promise<CallToolResponse> {
  if (!this.transport) {
    throw new Error('Not connected to Model Context Protocol server');
  }

  // @ts-expect-error: callTool response type is not compatible with CallToolResponse
  // eslint-disable-next-line
  const response: CallToolResponse = await this.client.callTool({
    name: tool,
    arguments: args,
  });

  return response;
}
}
